import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { EntitlementStatusSchema } from '../entitlement-status/entitlement-status';
import { EntitlementStoreSchema } from '../entitlement-store/entitlement-store';
import { ProductKeySchema } from '../product-key/product-key';

export const ENTITLEMENT_NOTE_MAX_LENGTH = 500;

/**
 * What one member is entitled to, from one store.
 *
 * The row is the platform's own record; for IAP the store is the authority and
 * this mirrors what the webhook last said. Nothing gates off it directly —
 * `EntitlementService` is the only reader that decides anything (N14).
 *
 * ## `productKey` and `tier` are SNAPSHOTS
 *
 * Copied when the entitlement is written, not joined to the product. A product
 * renamed, re-tiered or withdrawn must not silently change what an existing
 * subscriber is entitled to — the same reason a discount grant snapshots its
 * terms rather than reading through to its rule. `tier` in particular decides
 * `feature`-mode capabilities, so a join here would let an operator edit a
 * product and change what every current subscriber can do.
 */
export const UserEntitlementSchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema,
    store: EntitlementStoreSchema,
    productKey: ProductKeySchema,
    tier: z.string(),
    status: EntitlementStatusSchema,
    startedAt: z.iso.datetime(),
    /**
     * When access lapses. `null` only for a promo grant made without an end —
     * a comp that lasts until somebody revokes it, which App Review accounts
     * need and no paid channel ever produces.
     */
    currentPeriodEnd: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    /**
     * The store's own identifier for the subscription — RevenueCat's original
     * transaction id, or the direct order's id. Opaque here; it exists so a
     * webhook replay and a support question can both find the right row.
     */
    externalRef: z.string().nullable(),
    /** Why a promo grant was made, or why an entitlement was revoked. */
    note: z.string().nullable(),
    /** The staff member who granted or revoked, for a `promo` or `direct` row. */
    grantedByUserId: UuidSchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type UserEntitlement = z.infer<typeof UserEntitlementSchema>;

/**
 * The member's own view — "am I subscribed, to what, until when?".
 *
 * `userId`, `grantedByUserId` and `externalRef` are gone: who granted a comp and
 * which transaction backs it are facts about the platform's bookkeeping, not
 * about the member's subscription. `productName` replaces the raw key for the
 * same reason a grant's `appliesToProductNames` does — a member should never be
 * shown operator shorthand.
 *
 * `isActive` is computed by the server rather than derived on the client from
 * `status` and `currentPeriodEnd`: that derivation is exactly the second opinion
 * N14 forbids, and a client clock running fast would grant itself a day.
 */
export const MemberEntitlementSchema = z
  .object({
    store: EntitlementStoreSchema,
    productKey: ProductKeySchema,
    productName: z.string(),
    tier: z.string(),
    status: EntitlementStatusSchema,
    startedAt: z.iso.datetime(),
    currentPeriodEnd: z.iso.datetime().nullable(),
    isActive: z.boolean(),
  })
  .strict();

export type MemberEntitlement = z.infer<typeof MemberEntitlementSchema>;
